import {
  Badge,
  Box,
  Center,
  Checkbox,
  Flex,
  Grid,
  HStack,
  Portal,
  Progress,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { CalendarIcon, TimeIcon } from "@chakra-ui/icons";
import { Instant, LocalDate } from "@js-joda/core";
import React from "react";
import { EntityProfileErrorState } from "../../components/EntityProfileFeedback";
import { entityProfileModalColors } from "../../entityProfileModal.theme";
import EntityModalButton from "../../components/EntityModalButton";
import useProfileWindowSettings from "../../../profileWindowSettings/useProfileWindowSettings";
import EntityProfileCalendarControls, {
  clippedActionsScrollSx,
} from "./EntityProfileCalendarControls";
import {
  chunkEntityCalendarWeeks,
  formatEntityCalendarDate,
  formatEntityCalendarCountdown,
  formatEntityCalendarDuration,
  formatEntityCalendarTime,
  getEntityCalendarDurationSeconds,
  getEntityCalendarNoteDateKey,
  getEntityCalendarVacationDateKey,
  getEntityCalendarVisitDateKey,
  getEntityCalendarBroadcastStatusLabel,
  getEntityCalendarVisitBroadcastStatus,
  toEntityCalendarLocalDate,
  toEntityCalendarLocalDateTime,
  type EntityCalendarBroadcastStatus,
  type EntityProfileCalendarMarker,
  type EntityProfileCalendarNote,
  type EntityProfileCalendarPtoRow,
  type EntityProfileCalendarVacation,
  type EntityProfileCalendarVisit,
} from "./entityProfileCalendar.utils";
import AnimatedBroadcastWavesIcon from "../../../../shared/icons/AnimatedBroadcastWavesIcon";
import useCountdown from "../../../../shared/hooks/useCountdown";
import RangeDatePicker from "../../../../shared/components/DatePicker/RangeDatePicker";
import Select from "../../../../shared/components/Select";

const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

type CalendarView = "calendar" | "list";
type CalendarSummaryVariant = "caregiver" | "default";
type CalendarListVariant = "caregiver" | "default";
type CalendarListStatusFilter = string[];
type CalendarListTypeFilter = string[];
type CalendarListSortKey = "address" | "date" | "caregiver" | "type";
type CalendarListSortDirection = "asc" | "desc";
type CalendarListPageSize = 15 | 25 | 50 | 100;
type CalendarListItem =
  | { kind: "marker"; marker: EntityProfileCalendarMarker }
  | { kind: "pto"; pto: EntityProfileCalendarPtoRow }
  | { kind: "visit"; visit: EntityProfileCalendarVisit }
  | { kind: "vacation"; vacation: EntityProfileCalendarVacation };
type EntityProfileCalendarDayMenuItem = {
  isDisabled?: boolean;
  label: string;
  onClick: () => void;
};
type EntityProfileCalendarDayMenuContext = {
  date: LocalDate;
  notes: EntityProfileCalendarNote[];
  vacations: EntityProfileCalendarVacation[];
  visits: EntityProfileCalendarVisit[];
};

function CalendarLoadingBar(props: { isActive: boolean }) {
  return (
    <Box
      bg={props.isActive ? "#bfdbfe" : "transparent"}
      h="4px"
      overflow="hidden"
      sx={{
        "@keyframes entityCalendarLoadingBar": {
          "0%": { transform: "translateX(-100%)" },
          "50%": { transform: "translateX(85%)" },
          "100%": { transform: "translateX(240%)" },
        },
      }}
    >
      <Box
        animation={props.isActive ? "entityCalendarLoadingBar 1.35s ease-in-out infinite" : "none"}
        bg={entityProfileModalColors.highlight}
        h="100%"
        opacity={props.isActive ? 1 : 0}
        transform="translateX(-100%)"
        transition="opacity 120ms ease"
        w="34%"
      />
    </Box>
  );
}

type Props = {
  actionButtons?: React.ReactNode;
  selectionControls?: React.ReactNode;
  dayMenuItems?: (
    context: EntityProfileCalendarDayMenuContext
  ) => EntityProfileCalendarDayMenuItem[];
  errorLabel: string;
  isError: boolean;
  isLoading: boolean;
  listActionButtons?: React.ReactNode;
  listCertificationOptions?: string[];
  listDateRange?: [LocalDate, LocalDate | null];
  listVariant?: CalendarListVariant;
  monthDate: LocalDate;
  markers: EntityProfileCalendarMarker[];
  notes: EntityProfileCalendarNote[];
  onMonthDateChange: (monthDate: LocalDate) => void;
  onListDateRangeChange?: (range: [LocalDate, LocalDate | null]) => void;
  onListVisibleSelectionChange?: (selection: {
    markerIds: string[];
    vacationIds: EntityProfileCalendarVacation["id"][];
    visitIds: EntityProfileCalendarVisit["id"][];
  }) => void;
  onToggleMarkerSelection?: (marker: EntityProfileCalendarMarker) => void;
  onRetry: () => void;
  onPatientClick?: (patientId: NonNullable<EntityProfileCalendarVisit["patientId"]>) => void;
  onToggleVacationSelection?: (vacation: EntityProfileCalendarVacation) => void;
  onToggleVisitSelection?: (visit: EntityProfileCalendarVisit) => void;
  onVisitClick?: (visit: EntityProfileCalendarVisit) => void;
  onVisitNameClick?: (visit: EntityProfileCalendarVisit) => void;
  ptoRows?: EntityProfileCalendarPtoRow[];
  selectedMarkerIds?: string[];
  selectedVacationIds?: EntityProfileCalendarVacation["id"][];
  selectedVisitIds?: EntityProfileCalendarVisit["id"][];
  showAuthorizationDetails?: boolean;
  showCaregiverOvertimeAlerts?: boolean;
  showSummaryColumn?: boolean;
  summaryVariant?: CalendarSummaryVariant;
  vacations: EntityProfileCalendarVacation[];
  view: CalendarView;
  visibleDates: LocalDate[];
  visits: EntityProfileCalendarVisit[];
};

type WeekSummary = {
  assignedScheduledSeconds: number;
  billedSeconds: number;
  paidSeconds: number;
  ptoSeconds: number;
  recordedSeconds: number;
  scheduledSeconds: number;
};

const caregiverOvertimeWarnHours = 51;
const caregiverOvertimeCapHours = 56;

const calendarListStatusOptions: Array<{ value: string; label: string }> = [
  { value: "Assigned Visit", label: "Assigned Visit" },
  { value: "Unstaffed", label: "Unstaffed" },
  { value: "Task", label: "Task" },
  { value: "Missed", label: "Missed" },
  { value: "Removed", label: "Removed" },
  { value: "No Authorization", label: "No Authorization" },
  { value: "Paid time off", label: "Paid time off" },
  { value: "Broadcasted", label: "Broadcasted" },
  { value: "Pending Requests", label: "Pending Requests" },
  { value: "Awaiting Assignment", label: "Awaiting Assignment" },
  { value: "ON VACATION", label: "ON VACATION" },
  { value: "In Absence", label: "In Absence" },
];

const calendarListSelectButtonProps = {
  _hover: { bg: "#fff", borderColor: "#adadad" },
  bg: "#fff",
  borderColor: "#ccc",
  borderRadius: "2px",
  color: "#333",
  fontSize: "15px",
  fontWeight: 400,
  h: "38px",
  minW: 0,
  px: "14px",
  w: "100%",
};
const calendarListPageSizes: CalendarListPageSize[] = [15, 25, 50, 100];

function getCalendarListHeaderStyles(isActive: boolean) {
  return {
    bg: isActive ? "#d9edf7" : undefined,
    borderColor: "#ddd",
    color: "#333",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: 400,
    textTransform: "none" as const,
  };
}

const calendarSelectionCheckboxSx = {
  ".chakra-checkbox__control": {
    bg: "rgba(255, 255, 255, 0.92)",
    border: "none",
    borderRadius: "6px",
    boxShadow: "none",
  },
  ".chakra-checkbox__control[data-checked]": {
    bg: "#3077EB",
    color: "#fff",
  },
  ".chakra-checkbox__control[data-hover]": {
    bg: "rgba(255, 255, 255, 0.92)",
  },
  ".chakra-checkbox__control[data-checked][data-hover]": {
    bg: "#3077EB",
  },
  ".chakra-checkbox__control[data-focus-visible]": {
    boxShadow: "0 0 0 2px rgba(48, 119, 235, 0.35)",
  },
};

function stopCalendarSelectionEvent(event: React.SyntheticEvent) {
  event.stopPropagation();
}

function groupByDate<TItem>(items: TItem[], getKey: (item: TItem) => string | null) {
  const grouped = new Map<string, TItem[]>();

  for (const item of items) {
    const key = getKey(item);

    if (key === null) {
      continue;
    }

    grouped.set(key, [...(grouped.get(key) ?? []), item]);
  }

  return grouped;
}

function getVisitStatusLabel(visit: EntityProfileCalendarVisit) {
  switch (visit.status) {
    case "deleted":
      return "Deleted";
    case "missed":
      return "Missed";
    case "task":
      return "Task";
    case "visit":
      return "Visit";
  }
}

function getCalendarListPrimaryStatus(visit: EntityProfileCalendarVisit) {
  switch (visit.status) {
    case "deleted":
      return "Removed";
    case "missed":
      return "Missed";
    case "task":
      return "Task";
    case "visit":
      return visit.title === "Unassigned" ? "Unstaffed" : "Assigned Visit";
  }
}

function getCalendarListStatuses(visit: EntityProfileCalendarVisit) {
  const statuses = [getCalendarListPrimaryStatus(visit)];

  if (visit.authorizationStatuses?.some((status) => status.message === "No Authorization")) {
    statuses.push("No Authorization");
  }

  // Keep the list in step with the cards: same rules, same labels.
  if (visit.broadcastKind === "broadcasted") {
    statuses.push("Broadcasted");

    const broadcastStatus = getEntityCalendarVisitBroadcastStatus(visit);

    if (broadcastStatus !== null) {
      statuses.push(getEntityCalendarBroadcastStatusLabel(broadcastStatus));
    }
  }

  return statuses;
}

function getCalendarListStatusTooltip(visit: EntityProfileCalendarVisit, status: string) {
  if (status === "Missed") {
    return visit.missedVisitReason ?? "Missed visit";
  }

  if (status === "No Authorization") {
    return visit.authorizationStatuses
      ?.filter((authorizationStatus) => authorizationStatus.tooltip)
      .map((authorizationStatus) => authorizationStatus.tooltip)
      .join("\n");
  }

  if (status === "Broadcasted") {
    return "This unstaffed visit has an active broadcast";
  }

  if (status === "Pending Requests") {
    return "Broadcast has no caregiver requests yet";
  }

  if (status === "Awaiting Assignment") {
    return `${visit.visitBroadcast?.requests ?? 0} caregiver request${
      visit.visitBroadcast?.requests === 1 ? "" : "s"
    }`;
  }

  if (status === "Pending Confirmation") {
    return "Waiting for a caregiver to confirm the broadcast";
  }

  return undefined;
}

function getCalendarListItemDate(item: CalendarListItem) {
  if (item.kind === "visit") {
    return toEntityCalendarLocalDate(item.visit.date);
  }

  if (item.kind === "marker") {
    return toEntityCalendarLocalDate(item.marker.date);
  }

  if (item.kind === "pto") {
    return toEntityCalendarLocalDate(item.pto.date);
  }

  return toEntityCalendarLocalDate(item.vacation.date);
}

function getCalendarListItemEndDate(item: CalendarListItem) {
  if (item.kind !== "marker" || item.marker.endDate === undefined) {
    return getCalendarListItemDate(item);
  }

  return toEntityCalendarLocalDate(item.marker.endDate);
}

function getCalendarListMarkerStatuses(marker: EntityProfileCalendarMarker) {
  if (String(marker.id).startsWith("absence-")) {
    return ["In Absence"];
  }

  if (marker.colorScheme === "purple") {
    return ["Broadcasted", marker.statusLabel ?? "Pending Requests"];
  }

  if (marker.colorScheme === "blue" || marker.colorScheme === "orange") {
    return ["Task"];
  }

  return [marker.statusLabel ?? marker.label];
}

function getCalendarListItemStatuses(item: CalendarListItem) {
  if (item.kind === "visit") {
    return getCalendarListStatuses(item.visit);
  }

  if (item.kind === "marker") {
    return getCalendarListMarkerStatuses(item.marker);
  }

  if (item.kind === "pto") {
    return ["Paid time off"];
  }

  return ["ON VACATION"];
}

function compareCalendarListItems(
  firstItem: CalendarListItem,
  secondItem: CalendarListItem,
  sortKey: CalendarListSortKey
) {
  if (firstItem.kind === "visit" && secondItem.kind === "visit") {
    return (
      compareCalendarListVisits(firstItem.visit, secondItem.visit, sortKey) ||
      compareVisitsByStartTime(firstItem.visit, secondItem.visit) ||
      firstItem.visit.id - secondItem.visit.id
    );
  }

  if (sortKey !== "date") {
    const firstValue =
      firstItem.kind === "visit"
        ? sortKey === "caregiver"
          ? firstItem.visit.title
          : firstItem.visit.certification ?? ""
        : firstItem.kind === "marker"
        ? sortKey === "caregiver"
          ? firstItem.marker.label
          : ""
        : firstItem.kind === "pto"
        ? sortKey === "caregiver"
          ? firstItem.pto.patientName ?? ""
          : firstItem.pto.certification ?? ""
        : "";
    const secondValue =
      secondItem.kind === "visit"
        ? sortKey === "caregiver"
          ? secondItem.visit.title
          : secondItem.visit.certification ?? ""
        : secondItem.kind === "marker"
        ? sortKey === "caregiver"
          ? secondItem.marker.label
          : ""
        : secondItem.kind === "pto"
        ? sortKey === "caregiver"
          ? secondItem.pto.patientName ?? ""
          : secondItem.pto.certification ?? ""
        : "";
    const comparison = firstValue.localeCompare(secondValue);

    if (comparison !== 0) {
      return comparison;
    }
  }

  const dateComparison = getCalendarListItemDate(firstItem).compareTo(
    getCalendarListItemDate(secondItem)
  );

  if (dateComparison !== 0) {
    return dateComparison;
  }

  const firstId =
    firstItem.kind === "visit"
      ? firstItem.visit.id
      : firstItem.kind === "marker"
      ? String(firstItem.marker.id)
      : firstItem.kind === "pto"
      ? firstItem.pto.id
      : firstItem.vacation.id;
  const secondId =
    secondItem.kind === "visit"
      ? secondItem.visit.id
      : secondItem.kind === "marker"
      ? String(secondItem.marker.id)
      : secondItem.kind === "pto"
      ? secondItem.pto.id
      : secondItem.vacation.id;

  return String(firstId).localeCompare(String(secondId));
}

function getCalendarListStatusStyle(status: string) {
  const statusStyleByLabel: Record<string, { backgroundColor: string; color: string }> = {
    "Assigned Visit": { backgroundColor: "#8CCC85", color: "#fff" },
    Unstaffed: { backgroundColor: "#FF8360", color: "#fff" },
    Missed: { backgroundColor: "#E66057", color: "#fff" },
    Removed: { backgroundColor: "#E66057", color: "#fff" },
    "No Authorization": { backgroundColor: "#E66057", color: "#fff" },
    "Paid time off": { backgroundColor: "#968CEC", color: "#fff" },
    Broadcasted: { backgroundColor: "#FCA85B", color: "#fff" },
    "Pending Requests": { backgroundColor: "#EBF4FF", color: "#3391FE" },
    "Awaiting Assignment": { backgroundColor: "#ff9800", color: "#fff" },
    "Pending Confirmation": { backgroundColor: "#FBFBE0", color: "#A4A30D" },
    "ON VACATION": { backgroundColor: "#fbcdff", color: "#b700c7" },
    "In Absence": { backgroundColor: "#fbcdff", color: "#b700c7" },
    Task: { backgroundColor: "#85C8CC", color: "#fff" },
  };

  return statusStyleByLabel[status] ?? { backgroundColor: "#8CCC85", color: "#fff" };
}

function getCalendarMarkerPalette(marker: EntityProfileCalendarMarker) {
  switch (marker.colorScheme) {
    case "green":
      return {
        bg: "#8CCC85",
        border: "#6FC066",
        color: "#fff",
        prefix: "",
      };
    case "red":
      return {
        bg: "#E66057",
        border: "#ce4d45",
        color: "#fff",
        prefix: "",
      };
    case "orange":
      return {
        bg: "#f8b45a",
        border: "#ec9b2f",
        color: "#fff",
        prefix: "[Task]",
      };
    case "purple":
      return {
        bg: "#bdafeb",
        border: "#a292da",
        color: "#fff",
        prefix: "",
      };
    case "blue":
    default:
      return {
        bg: "#85c8cc",
        border: "#6db6bb",
        color: "#fff",
        prefix: "[Task]",
      };
  }
}

function MarkerActionLinks(props: { marker: EntityProfileCalendarMarker }) {
  if (props.marker.actions === undefined || props.marker.actions.length === 0) {
    return null;
  }

  return (
    <>
      {props.marker.actions.map((action, index) => (
        <React.Fragment key={action.label}>
          {index > 0 ? (
            <Text as="span" mx={2}>
              ·
            </Text>
          ) : null}
          <EntityModalButton
            disabled={action.isDisabled}
            kind="blue"
            style={{ color: "#428bca", fontSize: "13px", fontWeight: 600 }}
            variant="link"
            onClick={(event) => {
              event.stopPropagation();
              action.onClick();
            }}
          >
            {action.label}
          </EntityModalButton>
        </React.Fragment>
      ))}
    </>
  );
}

function CalendarMarkerStrip(props: {
  marker: EntityProfileCalendarMarker;
  selectedMarkerIds?: Set<string>;
  onToggleMarkerSelection?: (marker: EntityProfileCalendarMarker) => void;
}) {
  const palette = getCalendarMarkerPalette(props.marker);
  const startDate = toEntityCalendarLocalDate(props.marker.date);
  const endDate =
    props.marker.endDate === undefined ? null : toEntityCalendarLocalDate(props.marker.endDate);
  const shortStartDate = `${startDate.dayOfMonth()} ${startDate.month().toString().slice(0, 3)}`;
  const dateLabel =
    endDate === null || endDate.equals(startDate)
      ? formatEntityCalendarDate(startDate)
      : `${formatEntityCalendarDate(startDate)} - ${formatEntityCalendarDate(endDate)}`;
  const detailText = props.marker.detailLabel ?? `Future (Unstaffed) · ${shortStartDate}`;
  const actionLinks = <MarkerActionLinks marker={props.marker} />;
  const canSelectMarker =
    props.onToggleMarkerSelection !== undefined && props.marker.summaryKind !== "pto";
  const selectionControl = canSelectMarker ? (
    <Box
      flexShrink={0}
      onClick={stopCalendarSelectionEvent}
      onKeyDown={stopCalendarSelectionEvent}
      onMouseDown={stopCalendarSelectionEvent}
    >
      <Checkbox
        isChecked={props.selectedMarkerIds?.has(String(props.marker.id)) ?? false}
        onChange={(event) => {
          event.stopPropagation();
          props.onToggleMarkerSelection?.(props.marker);
        }}
      />
    </Box>
  ) : null;

  if (props.marker.colorScheme === "purple") {
    return (
      <Box
        alignItems="center"
        bg={palette.bg}
        border={`1px solid ${palette.border}`}
        borderRadius="8px"
        color={palette.color}
        display="flex"
        fontSize="13px"
        fontWeight={600}
        gap="50px"
        minH="58px"
        px="14px"
        py="10px"
      >
        {selectionControl}
        <Text flexShrink={0} noOfLines={1} px="12px">
          {props.marker.label}
        </Text>
        <HStack flex={1} minW={0} spacing={3}>
          <Box
            alignItems="center"
            bg="#fff"
            borderRadius="8px"
            boxShadow="0 0 8px -5px rgb(0, 0, 0, 0.7) inset"
            color={
              props.marker.statusKind === undefined
                ? "#f0a018"
                : flexibleBroadcastStatusColors[props.marker.statusKind]
            }
            display="flex"
            flexShrink={0}
            fontSize="12px"
            fontWeight={600}
            gap="10px"
            justifyContent="center"
            pl="10px"
            pr="6px"
            py="6px"
            textAlign="center"
            w="200px"
          >
            {props.marker.statusLabel ?? "Awaiting Assignment"}
            {props.marker.statusKind === "AWAITING_ASSIGNMENT" ? (
              <Badge bg="#f8ac59" borderRadius="3px" color="#fff" fontSize="11px" px={2}>
                {props.marker.statusCount ?? 0}
              </Badge>
            ) : null}
            {props.marker.statusKind === "PENDING_CONFIRMATION" &&
            props.marker.pendingConfirmationUntil ? (
              <Badge bg="#1ab394" borderRadius="3px" color="#fff" fontSize="11px" px={2}>
                <CalendarBroadcastCountdown until={props.marker.pendingConfirmationUntil} />
              </Badge>
            ) : null}
          </Box>
          <Box
            aria-hidden
            as="i"
            className={props.marker.iconClassName ?? "fa fa-bullhorn"}
            flexShrink={0}
          />
          <Text noOfLines={1} whiteSpace="nowrap">
            <Text as="span">{detailText}</Text>
            <Text as="span" mx={2}>
              ·
            </Text>
            <Text as="span">{dateLabel}</Text>
            {props.marker.actions?.length ? (
              <>
                <Text as="span" mx={2}>
                  ·
                </Text>
                {actionLinks}
              </>
            ) : null}
          </Text>
        </HStack>
      </Box>
    );
  }

  return (
    <Box
      alignItems="center"
      bg={palette.bg}
      border={`1px solid ${palette.border}`}
      borderRadius="8px"
      boxShadow="inset 0 4px 10px rgba(112, 170, 173, 0.5)"
      color={palette.color}
      display="flex"
      fontSize="13px"
      fontWeight={700}
      minH="58px"
      px="14px"
      py="10px"
    >
      {selectionControl}
      <Text flexShrink={0} noOfLines={1} px="12px">
        {palette.prefix ? `${palette.prefix} ` : ""}
        {props.marker.label}
      </Text>
      <Box bg="#f4f4f4" borderRadius="8px" color="#3077EB" flex={1} minW={0} px={3} py="8px">
        <Text fontWeight={600} noOfLines={1} textAlign="center">
          {detailText}
          {props.marker.actions?.length ? (
            <>
              <Text as="span" mx={2}>
                ·
              </Text>
              {actionLinks}
            </>
          ) : null}
        </Text>
      </Box>
    </Box>
  );
}

function CalendarMarkerStrips(props: {
  markers: EntityProfileCalendarMarker[];
  selectedMarkerIds: string[];
  visibleDates: LocalDate[];
  onToggleMarkerSelection?: (marker: EntityProfileCalendarMarker) => void;
}) {
  const visibleStart = props.visibleDates[0];
  const visibleEnd = props.visibleDates[props.visibleDates.length - 1];
  const selectedMarkerIds = React.useMemo(
    () => new Set(props.selectedMarkerIds),
    [props.selectedMarkerIds]
  );
  const visibleMarkers = React.useMemo(() => {
    if (visibleStart === undefined || visibleEnd === undefined) {
      return [];
    }

    return props.markers
      .filter((marker) => {
        const markerStart = toEntityCalendarLocalDate(marker.date);
        const markerEnd =
          marker.endDate === undefined ? markerStart : toEntityCalendarLocalDate(marker.endDate);

        return (
          markerEnd.isBefore(visibleStart) === false && markerStart.isAfter(visibleEnd) === false
        );
      })
      .sort((firstMarker, secondMarker) => {
        const dateComparison = toEntityCalendarLocalDate(firstMarker.date).compareTo(
          toEntityCalendarLocalDate(secondMarker.date)
        );

        return dateComparison === 0
          ? String(firstMarker.id).localeCompare(String(secondMarker.id))
          : dateComparison;
      });
  }, [props.markers, visibleEnd, visibleStart]);

  if (visibleMarkers.length === 0) {
    return null;
  }

  return (
    <VStack alignItems="stretch" pb="8px" pt={0} spacing={3} w="100%">
      {visibleMarkers.map((marker) => (
        <CalendarMarkerStrip
          key={marker.id}
          marker={marker}
          selectedMarkerIds={selectedMarkerIds}
          onToggleMarkerSelection={props.onToggleMarkerSelection}
        />
      ))}
    </VStack>
  );
}

function getVisitCardPalette(visit: EntityProfileCalendarVisit) {
  switch (visit.broadcastKind ?? null) {
    case null:
      break;
    case "broadcasted":
      return {
        bg: "#FAB779",
        border: "transparent",
        boxShadow: "inset 0px 4px 10px #EBA96C",
        color: "white",
        statusColor: "white",
      };
    case "broadcastedInThePast":
      return {
        bg: "#FF8360",
        border: "#FE8462",
        boxShadow: "inset 0px 4px 12px #EA7352",
        color: "white",
        statusColor: "white",
      };
  }

  switch (visit.status) {
    case "deleted":
    case "missed":
      return {
        bg: "#F5F5F5",
        border: "#D5D5D5",
        color: entityProfileModalColors.title,
        boxShadow: "inset 0px 4px 12px rgba(213, 213, 213, 0.45)",
        statusColor: "#F04141",
      };
    case "task":
      return {
        bg: "#85C8CC",
        border: "#70AAAD",
        boxShadow: "inset 0px 4px 10px rgba(112, 170, 173, 0.5)",
        color: "white",
        statusColor: "white",
      };
    case "visit":
      return {
        bg: "#8CCC85",
        border: "#6FC066",
        boxShadow: "inset 0px 4px 10px rgba(111, 192, 102, 0.5)",
        color: "white",
        statusColor: "white",
      };
  }
}

function getScheduledSeconds(visit: EntityProfileCalendarVisit) {
  return getEntityCalendarDurationSeconds(visit.startTime, visit.endTime);
}

function getRecordedSeconds(visit: EntityProfileCalendarVisit) {
  return getEntityCalendarDurationSeconds(visit.clockinTime, visit.clockoutTime);
}

function compareVisitsByStartTime(
  firstVisit: EntityProfileCalendarVisit,
  secondVisit: EntityProfileCalendarVisit
) {
  const startTimeComparison = toEntityCalendarLocalDateTime(firstVisit.startTime).compareTo(
    toEntityCalendarLocalDateTime(secondVisit.startTime)
  );

  return startTimeComparison === 0 ? firstVisit.id - secondVisit.id : startTimeComparison;
}

function compareCalendarListVisits(
  firstVisit: EntityProfileCalendarVisit,
  secondVisit: EntityProfileCalendarVisit,
  sortKey: CalendarListSortKey
) {
  switch (sortKey) {
    case "date": {
      return toEntityCalendarLocalDateTime(firstVisit.startTime).compareTo(
        toEntityCalendarLocalDateTime(secondVisit.startTime)
      );
    }
    case "caregiver": {
      return firstVisit.title.localeCompare(secondVisit.title);
    }
    case "address": {
      return (firstVisit.patientAddress ?? "").localeCompare(secondVisit.patientAddress ?? "");
    }
    case "type": {
      return (firstVisit.certification ?? "").localeCompare(secondVisit.certification ?? "");
    }
  }
}

function getWeekSummary(
  visits: EntityProfileCalendarVisit[],
  markers: EntityProfileCalendarMarker[],
  weekDates: LocalDate[]
) {
  const weekDateKeys = new Set(weekDates.map((date) => date.toString()));
  const ptoSeconds = markers.reduce((total, marker) => {
    if (marker.summaryKind !== "pto" || marker.summarySeconds === undefined) {
      return total;
    }

    return weekDateKeys.has(toEntityCalendarLocalDate(marker.date).toString())
      ? total + marker.summarySeconds
      : total;
  }, 0);

  return visits.reduce<WeekSummary>(
    (summary, visit) => ({
      assignedScheduledSeconds:
        summary.assignedScheduledSeconds +
        (visit.status === "visit" && visit.title !== "Unassigned" ? getScheduledSeconds(visit) : 0),
      billedSeconds: summary.billedSeconds + (visit.billedSeconds ?? 0),
      paidSeconds: summary.paidSeconds + (visit.paidSeconds ?? 0),
      ptoSeconds: summary.ptoSeconds,
      recordedSeconds: summary.recordedSeconds + getRecordedSeconds(visit),
      scheduledSeconds: summary.scheduledSeconds + getScheduledSeconds(visit),
    }),
    {
      assignedScheduledSeconds: 0,
      billedSeconds: 0,
      paidSeconds: 0,
      ptoSeconds,
      recordedSeconds: 0,
      scheduledSeconds: 0,
    }
  );
}

function VisitTimeRow(props: {
  durationSeconds: number;
  endTime: EntityProfileCalendarVisit["endTime"];
  icon: React.ReactNode;
  iconLabel: string;
  startTime: EntityProfileCalendarVisit["startTime"];
}) {
  return (
    <Grid
      alignItems="center"
      columnGap="4px"
      fontSize="12px"
      templateColumns="14px minmax(0, 1fr) 34px"
    >
      <Tooltip label={props.iconLabel} placement="top">
        <Box justifySelf="center">{props.icon}</Box>
      </Tooltip>
      <Text fontWeight={700} minW={0} whiteSpace="nowrap">
        {formatEntityCalendarTime(props.startTime)} - {formatEntityCalendarTime(props.endTime)}
      </Text>
      <Text fontWeight={500} textAlign="right" whiteSpace="nowrap">
        {formatEntityCalendarDuration(props.durationSeconds)}
      </Text>
    </Grid>
  );
}

function isVisitSelectable(visit: EntityProfileCalendarVisit) {
  return visit.status !== "deleted" && visit.status !== "task";
}

function getVisitBatchTag(visit: EntityProfileCalendarVisit) {
  if (visit.visitBatchType === "SINGLE") {
    return "S";
  }

  if (visit.visitBatchType === "PERMANENT") {
    return visit.isOnWeeklyTemplate ? "P" : "T";
  }

  return null;
}

function getVisitBatchTooltip(tag: string) {
  switch (tag) {
    case "S":
      return "Single";
    case "P":
      return "Permanent";
    case "T":
      return "Temporary";
    default:
      return "";
  }
}

// custom.css gives the two places a broadcast status appears slightly different
// palettes - they agree on every state except PENDING_REQUESTS (:14562 vs :14593).
const flexibleBroadcastStatusColors: Record<EntityCalendarBroadcastStatus, string> = {
  AWAITING_ASSIGNMENT: "#FF7A00",
  PENDING_CONFIRMATION: "#A4A30D",
  PENDING_REQUESTS: "#d7ad04",
  UNSTAFFED: "#F04141",
};

const broadcastStatusColors: Record<EntityCalendarBroadcastStatus, string> = {
  AWAITING_ASSIGNMENT: "#FF7A00",
  PENDING_CONFIRMATION: "#A4A30D",
  PENDING_REQUESTS: "#FFCC00",
  UNSTAFFED: "#F04141",
};

function CalendarBroadcastCountdown(props: { until: Instant | string }) {
  const endsAt = props.until instanceof Instant ? props.until : Instant.parse(String(props.until));
  const secondsLeft = useCountdown({ endsAt, onTick: () => {} });

  return <>{formatEntityCalendarCountdown(secondsLeft ?? 0)}</>;
}

/** The pill Angular renders under a broadcasted visit's headline. */
function CalendarVisitBroadcastStatus(props: { visit: EntityProfileCalendarVisit }) {
  const status = getEntityCalendarVisitBroadcastStatus(props.visit);

  if (status === null) {
    return null;
  }

  return (
    <Box
      alignItems="center"
      bg="#fff"
      borderRadius="8px"
      boxShadow="0 0 8px -5px rgb(0, 0, 0, 0.7) inset"
      color={broadcastStatusColors[status]}
      display="flex"
      fontSize="12px"
      fontWeight={600}
      gap={2}
      justifyContent="center"
      mx="10px"
      my="8px"
      px="4px"
      py="6px"
      textAlign="center"
      title={status === "PENDING_CONFIRMATION" ? "Pending Confirmation" : undefined}
    >
      <Text as="span">{getEntityCalendarBroadcastStatusLabel(status)}</Text>
      {status === "AWAITING_ASSIGNMENT" ? (
        <Badge bg="#f8ac59" borderRadius="3px" color="#fff" fontSize="11px" px={2}>
          {props.visit.visitBroadcast?.requests ?? 0}
        </Badge>
      ) : null}
      {status === "PENDING_CONFIRMATION" && props.visit.pendingConfirmationUntil ? (
        <Badge bg="#1ab394" borderRadius="3px" color="#fff" fontSize="11px" px={2}>
          <CalendarBroadcastCountdown until={props.visit.pendingConfirmationUntil} />
        </Badge>
      ) : null}
    </Box>
  );
}

function CalendarVisitBadge(props: { children: React.ReactNode; label: string }) {
  // Angular's badge is a fixed 18px circle. The split tags ("1/2"/"2/2") are the one
  // case that cannot fit one, so they keep a pill; everything else matches exactly.
  const isSingleGlyph = typeof props.children === "string" && props.children.length === 1;

  return (
    <Tooltip label={props.label} placement="top">
      <Box
        alignItems="center"
        bg="#283847"
        borderRadius={isSingleGlyph ? "100%" : "999px"}
        color="#fff"
        display="inline-flex"
        fontSize="10px"
        fontWeight={600}
        h="18px"
        justifyContent="center"
        minW="18px"
        px={isSingleGlyph ? 0 : "5px"}
      >
        {props.children}
      </Box>
    </Tooltip>
  );
}

function VisitCard(props: {
  isSelected: boolean;
  showAuthorizationDetails: boolean;
  visit: EntityProfileCalendarVisit;
  onToggleSelection?: (visit: EntityProfileCalendarVisit) => void;
  onVisitClick?: (visit: EntityProfileCalendarVisit) => void;
  onVisitNameClick?: (visit: EntityProfileCalendarVisit) => void;
}) {
  const palette = getVisitCardPalette(props.visit);
  const scheduledSeconds = getScheduledSeconds(props.visit);
  const recordedSeconds = getRecordedSeconds(props.visit);
  const statusLabel = getVisitStatusLabel(props.visit);
  const broadcastKind = props.visit.broadcastKind ?? null;
  // Angular titles a live broadcast "Broadcasting" (with the animated bullhorn) and a
  // broadcast whose start has passed "Unstaffed", whatever the caregiver/task says.
  const title =
    broadcastKind === "broadcasted"
      ? "Broadcasting"
      : broadcastKind === "broadcastedInThePast"
      ? "Unstaffed"
      : props.visit.status === "visit"
      ? props.visit.title
      : statusLabel;
  const isSelectable = isVisitSelectable(props.visit);
  const batchTag = getVisitBatchTag(props.visit);
  const showBilled = (props.visit.billedSeconds ?? 0) > 0;
  const showPaid = (props.visit.paidSeconds ?? 0) > 0 || props.visit.isPaid === true;
  const showDraft =
    showPaid === false &&
    props.visit.payrollDraftSeconds !== undefined &&
    props.visit.payrollDraftSeconds > 0;
  const showBillingRow = showBilled || showPaid || showDraft;
  const hasBottomBadges =
    batchTag !== null ||
    props.visit.missedVisitReason != null ||
    (props.visit.missedDuties !== undefined && props.visit.missedDuties.length > 0) ||
    (props.visit.parentSplitVisitInstanceId !== null &&
      props.visit.parentSplitVisitInstanceId !== undefined) ||
    (props.visit.childSplitVisitInstanceId !== null &&
      props.visit.childSplitVisitInstanceId !== undefined);

  return (
    <Box
      bg={palette.bg}
      border={`1px solid ${palette.border}`}
      borderRadius="8px"
      boxShadow={
        props.isSelected ? `${palette.boxShadow ?? "none"}, 0 0 0 2px #1ab394` : palette.boxShadow
      }
      color={palette.color}
      cursor="pointer"
      display="flex"
      flexDirection="column"
      fontSize="12px"
      minH="128px"
      textAlign="start"
      onClick={(event) => {
        event.stopPropagation();
        props.onVisitClick?.(props.visit);
      }}
    >
      <HStack
        alignItems="center"
        justifyContent="space-between"
        mb="7px"
        mt="12px"
        mx="12px"
        spacing={1}
      >
        <Box minW={0}>
          <HStack spacing={2}>
            {broadcastKind === "broadcasted" ? (
              <AnimatedBroadcastWavesIcon boxSize="14px" flexShrink={0} />
            ) : null}
            <Text
              _hover={
                props.onVisitNameClick && !broadcastKind
                  ? { textDecoration: "underline" }
                  : undefined
              }
              as="button"
              color={palette.statusColor}
              fontSize="16px"
              fontWeight={700}
              lineHeight="1.2"
              textAlign="start"
              type="button"
              onClick={(event) => {
                if (props.onVisitNameClick && props.visit.status === "visit" && !broadcastKind) {
                  event.stopPropagation();
                  props.onVisitNameClick(props.visit);
                }
              }}
            >
              {title}
            </Text>
          </HStack>
          {props.visit.subtitle ? (
            <Text fontSize="12px" fontWeight={400} lineHeight="1.3" mt={0.5}>
              {props.visit.subtitle}
            </Text>
          ) : null}
        </Box>
        {isSelectable ? (
          <Box
            flexShrink={0}
            onClick={stopCalendarSelectionEvent}
            onKeyDown={stopCalendarSelectionEvent}
            onMouseDown={stopCalendarSelectionEvent}
          >
            <Checkbox
              colorScheme="blue"
              isChecked={props.isSelected}
              opacity={0.95}
              size="lg"
              sx={calendarSelectionCheckboxSx}
              onChange={(event) => {
                event.stopPropagation();
                props.onToggleSelection?.(props.visit);
              }}
            />
          </Box>
        ) : null}
      </HStack>
      <VStack alignItems="stretch" mb="8px" mt={0} mx="12px" spacing={1}>
        <VisitTimeRow
          durationSeconds={scheduledSeconds}
          endTime={props.visit.endTime}
          icon={<CalendarIcon boxSize={3.5} />}
          iconLabel="Scheduled"
          startTime={props.visit.startTime}
        />
        {recordedSeconds > 0 &&
        props.visit.clockinTime !== null &&
        props.visit.clockinTime !== undefined &&
        props.visit.clockoutTime !== null &&
        props.visit.clockoutTime !== undefined ? (
          <VisitTimeRow
            durationSeconds={recordedSeconds}
            endTime={props.visit.clockoutTime}
            icon={<TimeIcon boxSize={3.5} />}
            iconLabel="Recorded"
            startTime={props.visit.clockinTime}
          />
        ) : null}
      </VStack>
      <CalendarVisitBroadcastStatus visit={props.visit} />
      {hasBottomBadges ? (
        <HStack
          justifyContent="flex-end"
          mb={showBillingRow ? "4px" : "8px"}
          mt="auto"
          mx="12px"
          spacing={2}
        >
          {props.visit.childSplitVisitInstanceId ? (
            <CalendarVisitBadge label="This visit was split automatically by the system due to hhaExchange's overnight limits">
              1/2
            </CalendarVisitBadge>
          ) : null}
          {props.visit.parentSplitVisitInstanceId ? (
            <CalendarVisitBadge label="This visit was split automatically by the system due to hhaExchange's overnight limits">
              2/2
            </CalendarVisitBadge>
          ) : null}
          {props.visit.missedVisitReason ? (
            <CalendarVisitBadge label={props.visit.missedVisitReason}>M</CalendarVisitBadge>
          ) : null}
          {props.visit.missedDuties !== undefined && props.visit.missedDuties.length > 0 ? (
            <CalendarVisitBadge
              // States the fact, not an expectation. "Not yet assigned" reads as
              // pending, and a duty missed late in the week often cannot be made
              // up at all -- one missed on a Sunday never can, since the week has
              // no later visit to take it.
              label={`Clocked out without ${props.visit.missedDuties
                .map((duty) => duty.label)
                .join(", ")}. No make-up assigned on another visit this week.`}
            >
              D
            </CalendarVisitBadge>
          ) : null}
          {batchTag ? (
            <CalendarVisitBadge label={getVisitBatchTooltip(batchTag)}>
              {batchTag}
            </CalendarVisitBadge>
          ) : null}
        </HStack>
      ) : null}
      {showBillingRow ? (
        // Three slots, kept even when empty, so `space-between` puts billed hard left,
        // draft in the middle and paid hard right however many are showing - the
        // AngularJS row does the same with three always-rendered divs.
        <Flex
          fontSize="13px"
          fontWeight={400}
          justifyContent="space-between"
          m="4px"
          mt={hasBottomBadges ? "4px" : "auto"}
        >
          <Box whiteSpace="nowrap">
            {showBilled
              ? `Billed: ${formatEntityCalendarDuration(props.visit.billedSeconds)}`
              : null}
          </Box>
          <Box whiteSpace="nowrap">
            {showDraft
              ? `Draft: ${formatEntityCalendarDuration(props.visit.payrollDraftSeconds)}`
              : null}
          </Box>
          <Box whiteSpace="nowrap">
            {showPaid ? `Paid: ${formatEntityCalendarDuration(props.visit.paidSeconds)}` : null}
          </Box>
        </Flex>
      ) : null}
      {props.showAuthorizationDetails && props.visit.authorizationStatuses ? (
        <VStack alignItems="stretch" mb="8px" mx="10px" spacing="3px">
          {props.visit.authorizationStatuses.map((status, index) => (
            <Box
              key={`${status.message}-${index}`}
              bg={status.backgroundColor ?? "#fff"}
              borderRadius="6px"
              color={status.backgroundColor ? "#fff" : "#d9534f"}
              fontSize="11px"
              fontWeight={700}
              px="6px"
              py="4px"
              title={status.tooltip}
            >
              {status.message}
            </Box>
          ))}
        </VStack>
      ) : null}
    </Box>
  );
}

// Hover crosshair. Rather than recolouring cell borders, this lays a translucent
// blue *over* whatever background a cell already has, as a background-image on
// top of its background-color. Because it composites instead of replacing, the
// even/odd week striping and the summary column's overtime tints stay visible
// underneath in the same relative order — a flat blue fill would erase both.
const calendarHoverBandTint = "rgba(120, 144, 255, 0.12)";
const calendarHoverCellTint = "rgba(120, 144, 255, 0.22)";

const calendarHoverTint = (color: string) => ({
  backgroundImage: `linear-gradient(${color}, ${color})`,
});

const calendarColumnKeys = ["0", "1", "2", "3", "4", "5", "6", "summary"];

/**
 * Tint the whole column when either a day cell or the column header is hovered.
 * The day-cell arm excludes `:hover` so the hovered cell keeps its own stronger
 * tint; the header arm does not, so the header lights up with its column.
 */
const calendarHoverColumnSelector = calendarColumnKeys
  .flatMap((key) => [
    `:has(.entity-calendar-day-cell[data-day-index='${key}']:hover) .entity-calendar-column-cell[data-day-index='${key}']:not(:hover)`,
    `:has(.entity-calendar-header-cell[data-day-index='${key}']:hover) .entity-calendar-column-cell[data-day-index='${key}']`,
  ])
  .join(", ");

function CalendarDayCell(props: {
  dayIndex: number;
  date: LocalDate;
  isCurrentMonth: boolean;
  isEvenWeek: boolean;
  notes: EntityProfileCalendarNote[];
  selectedVacationIds: Set<EntityProfileCalendarVacation["id"]>;
  selectedVisitIds: Set<EntityProfileCalendarVisit["id"]>;
  vacations: EntityProfileCalendarVacation[];
  visits: EntityProfileCalendarVisit[];
  showAuthorizationDetails: boolean;
  onToggleVacationSelection?: (vacation: EntityProfileCalendarVacation) => void;
  onToggleVisitSelection?: (visit: EntityProfileCalendarVisit) => void;
  onVisitClick?: (visit: EntityProfileCalendarVisit) => void;
  onVisitNameClick?: (visit: EntityProfileCalendarVisit) => void;
  dayMenuItems?: EntityProfileCalendarDayMenuItem[];
}) {
  const borderColor = props.isCurrentMonth ? "#dadada" : "#e0e0e0";
  const dayTextColor = props.isCurrentMonth ? entityProfileModalColors.title : "#aeb7c4";
  const hasDayMenuItems = props.dayMenuItems !== undefined && props.dayMenuItems.length > 0;
  // Open the day's action menu at the click position, like the AngularJS
  // calendar: a fixed-positioned dropdown in a portal with a click-away overlay,
  // flipped away from the right/bottom edges so it stays on screen.
  const [menuPosition, setMenuPosition] = React.useState<{ x: number; y: number } | null>(null);
  const menuItemCount = (props.dayMenuItems?.length ?? 0) + 1;

  const openDayMenuAtEvent = (event: React.MouseEvent<HTMLDivElement>) => {
    const menuWidth = 190;
    const menuHeight = menuItemCount * 36 + 24;
    const x =
      event.clientX + menuWidth > window.innerWidth ? event.clientX - menuWidth : event.clientX;
    const y =
      event.clientY + menuHeight > window.innerHeight
        ? Math.max(8, event.clientY - menuHeight)
        : event.clientY;

    setMenuPosition({ x, y });
  };

  React.useEffect(() => {
    if (menuPosition === null) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuPosition(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [menuPosition]);

  return (
    <Box
      bg={props.isEvenWeek ? "rgba(0, 0, 0, 0.025)" : "white"}
      borderBottom={`1px solid ${borderColor}`}
      borderLeft="1px solid transparent"
      borderRight={`1px solid ${borderColor}`}
      borderTop="1px solid transparent"
      className="entity-calendar-column-cell entity-calendar-day-cell"
      cursor={hasDayMenuItems ? "pointer" : undefined}
      data-day-index={props.dayIndex}
      minH="min(11.8vw, 150px)"
      p={0}
      position="relative"
      onClick={hasDayMenuItems ? openDayMenuAtEvent : undefined}
    >
      <HStack justifyContent="flex-end" p="8px">
        <Box h="18px" minW="18px">
          <Text color={dayTextColor} fontSize="13px" fontWeight={400} lineHeight="1">
            {props.date.dayOfMonth()}
          </Text>
        </Box>
      </HStack>
      {hasDayMenuItems && menuPosition !== null ? (
        <Portal>
          <Box
            bottom={0}
            left={0}
            position="fixed"
            right={0}
            top={0}
            zIndex={1500}
            // stopPropagation is essential: this overlay is portaled, and React
            // bubbles portal events through the component tree back to the day
            // cell's onClick — without it, every click here reopens the menu.
            onClick={(event) => {
              event.stopPropagation();
              setMenuPosition(null);
            }}
            onContextMenu={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setMenuPosition(null);
            }}
          >
            <VStack
              alignItems="stretch"
              bg="white"
              borderRadius="8px"
              boxShadow="0 6px 20px rgba(0, 0, 0, 0.18)"
              left={`${menuPosition.x}px`}
              minW="184px"
              position="fixed"
              py={2}
              spacing={0}
              top={`${menuPosition.y}px`}
              onClick={(event) => event.stopPropagation()}
            >
              {props.dayMenuItems?.map((item) => (
                <Box
                  key={item.label}
                  _hover={item.isDisabled ? undefined : { bg: "#f2f4f7" }}
                  as="button"
                  color={item.isDisabled ? "#a0aec0" : entityProfileModalColors.text}
                  cursor={item.isDisabled ? "not-allowed" : "pointer"}
                  fontSize="13px"
                  px={4}
                  py={2}
                  textAlign="left"
                  w="100%"
                  onClick={() => {
                    if (item.isDisabled === true) {
                      return;
                    }
                    setMenuPosition(null);
                    item.onClick();
                  }}
                >
                  {item.label}
                </Box>
              ))}
              <Box borderTopColor="#e2e8f0" borderTopWidth="1px" my={1} />
              <Box
                _hover={{ bg: "#f2f4f7" }}
                as="button"
                color={entityProfileModalColors.text}
                fontSize="13px"
                px={4}
                py={2}
                textAlign="left"
                w="100%"
                onClick={() => setMenuPosition(null)}
              >
                Close
              </Box>
            </VStack>
          </Box>
        </Portal>
      ) : null}
      <VStack
        alignItems="stretch"
        m="2px"
        opacity={props.isCurrentMonth ? 1 : 0.7}
        spacing="2px"
        textAlign="center"
      >
        {props.vacations.map((vacation) => (
          <Box
            key={vacation.id}
            alignItems="center"
            bg="#fbcdff"
            border="1px solid transparent"
            borderRadius="8px"
            boxShadow={props.selectedVacationIds.has(vacation.id) ? "0 0 0 2px #b700c7" : undefined}
            color="#b700c7"
            display="grid"
            fontSize="13px"
            fontWeight={700}
            gridTemplateColumns="18px 1fr 22px"
            lineHeight="1.2"
            m="2px"
            minH="48px"
            px="10px"
            textTransform="uppercase"
            onClick={stopCalendarSelectionEvent}
          >
            <Box
              aria-hidden
              as="img"
              color="currentColor"
              h="16px"
              src="admin/images/icons/calendar-time-off.svg"
              w="16px"
            />
            <Text textAlign="center">Vacation</Text>
            <Box
              justifySelf="end"
              onClick={stopCalendarSelectionEvent}
              onKeyDown={stopCalendarSelectionEvent}
              onMouseDown={stopCalendarSelectionEvent}
            >
              <Checkbox
                colorScheme="blue"
                isChecked={props.selectedVacationIds.has(vacation.id)}
                size="lg"
                sx={calendarSelectionCheckboxSx}
                onChange={(event) => {
                  event.stopPropagation();
                  props.onToggleVacationSelection?.(vacation);
                }}
              />
            </Box>
          </Box>
        ))}
        {props.visits.map((visit) => (
          <VisitCard
            key={visit.id}
            isSelected={props.selectedVisitIds.has(visit.id)}
            showAuthorizationDetails={props.showAuthorizationDetails}
            visit={visit}
            onToggleSelection={props.onToggleVisitSelection}
            onVisitClick={props.onVisitClick}
            onVisitNameClick={props.onVisitNameClick}
          />
        ))}
        {props.notes.length > 0 ? (
          <Badge
            alignSelf="stretch"
            bg="#ffe282"
            borderRadius="4px"
            color="#c79800"
            fontSize="10px"
            fontWeight={600}
            lineHeight="1.2"
            m="2px"
            p="3px"
            textTransform="uppercase"
          >
            {props.notes.length} note{props.notes.length === 1 ? "" : "s"}
          </Badge>
        ) : null}
      </VStack>
    </Box>
  );
}

function WeekSummaryCell(props: {
  isEvenWeek: boolean;
  summary: WeekSummary;
  summaryVariant: CalendarSummaryVariant;
  showCaregiverOvertimeAlerts: boolean;
}) {
  const caregiverOvertimeSeverity =
    props.showCaregiverOvertimeAlerts &&
    props.summary.assignedScheduledSeconds >= caregiverOvertimeCapHours * 3600
      ? "cap"
      : props.showCaregiverOvertimeAlerts &&
        props.summary.assignedScheduledSeconds > caregiverOvertimeWarnHours * 3600
      ? "warn"
      : "none";
  const backgroundColor =
    caregiverOvertimeSeverity === "cap"
      ? "#fff1f0"
      : caregiverOvertimeSeverity === "warn"
      ? "#fff7e6"
      : props.isEvenWeek
      ? "rgba(0, 0, 0, 0.025)"
      : "white";

  return (
    <Box
      bg={backgroundColor}
      borderBottom="1px solid #dadada"
      borderLeft="1px solid transparent"
      borderRight="1px solid #dadada"
      borderTop="1px solid transparent"
      className="entity-calendar-column-cell entity-calendar-day-cell"
      color={entityProfileModalColors.title}
      data-day-index="summary"
      fontSize="13px"
      minH="min(11.8vw, 150px)"
      p="9px"
    >
      <Text>
        Scheduled: <b>{formatEntityCalendarDuration(props.summary.scheduledSeconds)}</b>
      </Text>
      <Text>
        Recorded: <b>{formatEntityCalendarDuration(props.summary.recordedSeconds)}</b>
      </Text>
      {props.summaryVariant === "caregiver" ? (
        <>
          <Text>
            PTO: <b>{formatEntityCalendarDuration(props.summary.ptoSeconds)}</b>
          </Text>
          {caregiverOvertimeSeverity === "warn" ? (
            <Text color="#b7791f" fontWeight={700} mt="8px">
              Approaching overtime cap ({caregiverOvertimeCapHours}h)
            </Text>
          ) : null}
          {caregiverOvertimeSeverity === "cap" ? (
            <Text color="#c53030" fontWeight={700} mt="8px">
              Overtime cap reached ({caregiverOvertimeCapHours}h)
            </Text>
          ) : null}
        </>
      ) : (
        <>
          <Text>
            Paid: <b>{formatEntityCalendarDuration(props.summary.paidSeconds)}</b>
          </Text>
          <Text>
            Billed: <b>{formatEntityCalendarDuration(props.summary.billedSeconds)}</b>
          </Text>
        </>
      )}
    </Box>
  );
}

function CalendarGridView(props: {
  dayMenuItems?: (
    context: EntityProfileCalendarDayMenuContext
  ) => EntityProfileCalendarDayMenuItem[];
  monthDate: LocalDate;
  markers: EntityProfileCalendarMarker[];
  notes: EntityProfileCalendarNote[];
  selectedMarkerIds: string[];
  selectedVacationIds: EntityProfileCalendarVacation["id"][];
  selectedVisitIds: EntityProfileCalendarVisit["id"][];
  showAuthorizationDetails: boolean;
  showCaregiverOvertimeAlerts: boolean;
  showSummaryColumn: boolean;
  summaryVariant: CalendarSummaryVariant;
  vacations: EntityProfileCalendarVacation[];
  visibleDates: LocalDate[];
  visits: EntityProfileCalendarVisit[];
  onToggleMarkerSelection?: (marker: EntityProfileCalendarMarker) => void;
  onToggleVacationSelection?: (vacation: EntityProfileCalendarVacation) => void;
  onToggleVisitSelection?: (visit: EntityProfileCalendarVisit) => void;
  onVisitClick?: (visit: EntityProfileCalendarVisit) => void;
  onVisitNameClick?: (visit: EntityProfileCalendarVisit) => void;
}) {
  const weeks = React.useMemo(
    () => chunkEntityCalendarWeeks(props.visibleDates),
    [props.visibleDates]
  );
  const visitsByDate = React.useMemo(
    () => groupByDate(props.visits, getEntityCalendarVisitDateKey),
    [props.visits]
  );
  const vacationsByDate = React.useMemo(
    () => groupByDate(props.vacations, getEntityCalendarVacationDateKey),
    [props.vacations]
  );
  const notesByDate = React.useMemo(
    () => groupByDate(props.notes, getEntityCalendarNoteDateKey),
    [props.notes]
  );
  const selectedVisitIds = React.useMemo(
    () => new Set(props.selectedVisitIds),
    [props.selectedVisitIds]
  );
  const selectedVacationIds = React.useMemo(
    () => new Set(props.selectedVacationIds),
    [props.selectedVacationIds]
  );

  const columnCount = props.showSummaryColumn ? 8 : 7;
  const templateColumns = `repeat(${columnCount}, minmax(0, 1fr))`;
  // Opt-in: the Angular calendar has no hover highlight, so this stays off
  // unless the user turns it on in the profile window settings.
  const [{ showCalendarHoverIndication }] = useProfileWindowSettings();

  return (
    <Box>
      <CalendarMarkerStrips
        markers={props.markers}
        selectedMarkerIds={props.selectedMarkerIds}
        visibleDates={props.visibleDates}
        onToggleMarkerSelection={props.onToggleMarkerSelection}
      />
      <Box overflowX="auto">
        <Box minW={props.showSummaryColumn ? "1560px" : "1365px"}>
          <Box
            borderLeft="1px solid #eeeeee"
            borderTop="1px solid #eeeeee"
            sx={
              showCalendarHoverIndication
                ? {
                    ".entity-calendar-week-row:has(.entity-calendar-day-cell:hover) .entity-calendar-day-cell:not(:hover)":
                      calendarHoverTint(calendarHoverBandTint),
                    [calendarHoverColumnSelector]: calendarHoverTint(calendarHoverBandTint),
                    ".entity-calendar-day-cell:hover": calendarHoverTint(calendarHoverCellTint),
                  }
                : undefined
            }
          >
            <Grid
              bg="white"
              color="#0f172a"
              fontSize="14px"
              fontWeight={700}
              templateColumns={templateColumns}
              textAlign="left"
              textTransform="uppercase"
            >
              {dayLabels.map((day) => (
                <Box
                  key={day}
                  borderBottom="1px solid #eeeeee"
                  borderLeft="1px solid transparent"
                  borderRight="1px solid #eeeeee"
                  borderTop="1px solid transparent"
                  className="entity-calendar-column-cell entity-calendar-header-cell"
                  data-day-index={dayLabels.indexOf(day)}
                  p="8px"
                >
                  {day}
                </Box>
              ))}
              {props.showSummaryColumn ? (
                <Box
                  borderBottom="1px solid #eeeeee"
                  borderLeft="1px solid transparent"
                  borderRight="1px solid #eeeeee"
                  borderTop="1px solid transparent"
                  className="entity-calendar-column-cell entity-calendar-header-cell"
                  data-day-index="summary"
                  p="8px"
                >
                  Summary
                </Box>
              ) : null}
            </Grid>
            {weeks.map((week, weekIndex) => {
              const weekVisits = week.flatMap((date) => visitsByDate.get(date.toString()) ?? []);
              const isEvenWeek = (weekIndex + 1) % 2 === 0;

              return (
                <Grid
                  key={week[0].toString()}
                  bg="white"
                  className="entity-calendar-week-row"
                  templateColumns={templateColumns}
                >
                  {week.map((date, dayIndex) => (
                    <CalendarDayCell
                      key={date.toString()}
                      date={date}
                      dayIndex={dayIndex}
                      dayMenuItems={props.dayMenuItems?.({
                        date,
                        notes: notesByDate.get(date.toString()) ?? [],
                        vacations: vacationsByDate.get(date.toString()) ?? [],
                        visits: visitsByDate.get(date.toString()) ?? [],
                      })}
                      isCurrentMonth={date.monthValue() === props.monthDate.monthValue()}
                      isEvenWeek={isEvenWeek}
                      notes={notesByDate.get(date.toString()) ?? []}
                      selectedVacationIds={selectedVacationIds}
                      selectedVisitIds={selectedVisitIds}
                      showAuthorizationDetails={props.showAuthorizationDetails}
                      vacations={vacationsByDate.get(date.toString()) ?? []}
                      visits={visitsByDate.get(date.toString()) ?? []}
                      onToggleVacationSelection={props.onToggleVacationSelection}
                      onToggleVisitSelection={props.onToggleVisitSelection}
                      onVisitClick={props.onVisitClick}
                      onVisitNameClick={props.onVisitNameClick}
                    />
                  ))}
                  {props.showSummaryColumn ? (
                    <WeekSummaryCell
                      isEvenWeek={isEvenWeek}
                      showCaregiverOvertimeAlerts={props.showCaregiverOvertimeAlerts}
                      summary={getWeekSummary(weekVisits, props.markers, week)}
                      summaryVariant={props.summaryVariant}
                    />
                  ) : null}
                </Grid>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function CalendarListView(props: {
  isLoading: boolean;
  listActionButtons?: React.ReactNode;
  listCertificationOptions?: string[];
  listDateRange: [LocalDate, LocalDate | null];
  listVariant: CalendarListVariant;
  markers: EntityProfileCalendarMarker[];
  ptoRows: EntityProfileCalendarPtoRow[];
  selectedMarkerIds: string[];
  selectedVacationIds: EntityProfileCalendarVacation["id"][];
  selectedVisitIds: EntityProfileCalendarVisit["id"][];
  vacations: EntityProfileCalendarVacation[];
  visits: EntityProfileCalendarVisit[];
  onListDateRangeChange?: (range: [LocalDate, LocalDate | null]) => void;
  onListVisibleSelectionChange?: (selection: {
    markerIds: string[];
    vacationIds: EntityProfileCalendarVacation["id"][];
    visitIds: EntityProfileCalendarVisit["id"][];
  }) => void;
  onPatientClick?: (patientId: NonNullable<EntityProfileCalendarVisit["patientId"]>) => void;
  onToggleMarkerSelection?: (marker: EntityProfileCalendarMarker) => void;
  onToggleVacationSelection?: (vacation: EntityProfileCalendarVacation) => void;
  onToggleVisitSelection?: (visit: EntityProfileCalendarVisit) => void;
  onVisitClick?: (visit: EntityProfileCalendarVisit) => void;
  onVisitNameClick?: (visit: EntityProfileCalendarVisit) => void;
}) {
  const onListVisibleSelectionChange = props.onListVisibleSelectionChange;
  const [rangeStart, rangeEnd] = props.listDateRange;
  const [draftRange, setDraftRange] = React.useState<[LocalDate, LocalDate | null]>(
    props.listDateRange
  );
  const [statusFilter, setStatusFilter] = React.useState<CalendarListStatusFilter>([]);
  const [typeFilter, setTypeFilter] = React.useState<CalendarListTypeFilter>([]);
  const [sortKey, setSortKey] = React.useState<CalendarListSortKey>("date");
  const [sortDirection, setSortDirection] = React.useState<CalendarListSortDirection>("asc");
  const [pageSize, setPageSize] = React.useState<CalendarListPageSize>(15);
  const [pageIndex, setPageIndex] = React.useState(0);
  const selectedVisitIds = React.useMemo(
    () => new Set(props.selectedVisitIds),
    [props.selectedVisitIds]
  );
  const selectedMarkerIds = React.useMemo(
    () => new Set(props.selectedMarkerIds),
    [props.selectedMarkerIds]
  );
  const selectedVacationIds = React.useMemo(
    () => new Set(props.selectedVacationIds),
    [props.selectedVacationIds]
  );
  const typeOptions = React.useMemo(
    () =>
      (props.listCertificationOptions ?? []).map((certification) => ({
        label: certification,
        value: certification,
      })),
    [props.listCertificationOptions]
  );

  const filteredItems = React.useMemo(() => {
    const listItems: CalendarListItem[] = [
      ...props.markers
        .filter((marker) => marker.summaryKind !== "pto")
        .map((marker) => ({ kind: "marker" as const, marker })),
      ...props.visits
        .filter((visit) => props.listVariant !== "caregiver" || visit.status !== "deleted")
        .map((visit) => ({ kind: "visit" as const, visit })),
      ...props.ptoRows.map((pto) => ({ kind: "pto" as const, pto })),
      ...props.vacations.map((vacation) => ({ kind: "vacation" as const, vacation })),
    ];
    const filtered = listItems.filter((item) => {
      const itemDate = getCalendarListItemDate(item);
      const itemEndDate = getCalendarListItemEndDate(item);

      if (itemEndDate.isBefore(rangeStart)) {
        return false;
      }

      if (rangeEnd !== null && itemDate.isAfter(rangeEnd)) {
        return false;
      }

      const itemStatuses = getCalendarListItemStatuses(item);

      if (
        statusFilter.length > 0 &&
        itemStatuses.some((status) => statusFilter.includes(status)) === false
      ) {
        return false;
      }

      if (item.kind === "vacation" || item.kind === "marker") {
        return typeFilter.length === 0;
      }

      if (item.kind === "pto") {
        return (
          typeFilter.length === 0 ||
          (item.pto.certification !== null &&
            item.pto.certification !== undefined &&
            typeFilter.includes(item.pto.certification))
        );
      }

      if (typeFilter.length === 0) {
        return true;
      }

      return item.visit.certification !== null && item.visit.certification !== undefined
        ? typeFilter.includes(item.visit.certification)
        : false;
    });

    return filtered.sort((firstItem, secondItem) => {
      const comparison = compareCalendarListItems(firstItem, secondItem, sortKey);

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [
    props.vacations,
    props.markers,
    props.ptoRows,
    props.visits,
    props.listVariant,
    rangeEnd,
    rangeStart,
    sortDirection,
    sortKey,
    statusFilter,
    typeFilter,
  ]);
  const pageCount = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const filteredSelectionIds = React.useMemo(
    () =>
      filteredItems.reduce<{
        markerIds: string[];
        vacationIds: EntityProfileCalendarVacation["id"][];
        visitIds: EntityProfileCalendarVisit["id"][];
      }>(
        (selection, item) => {
          if (item.kind === "visit") {
            selection.visitIds.push(item.visit.id);
          } else if (item.kind === "vacation") {
            selection.vacationIds.push(item.vacation.id);
          } else if (item.kind === "marker") {
            selection.markerIds.push(String(item.marker.id));
          }

          return selection;
        },
        { markerIds: [], vacationIds: [], visitIds: [] }
      ),
    [filteredItems]
  );
  const visibleItems = React.useMemo(() => {
    const pageStart = pageIndex * pageSize;

    return filteredItems.slice(pageStart, pageStart + pageSize);
  }, [filteredItems, pageIndex, pageSize]);
  React.useEffect(() => {
    setPageIndex((currentPageIndex) => Math.min(currentPageIndex, pageCount - 1));
  }, [pageCount]);
  React.useEffect(() => {
    setDraftRange(props.listDateRange);
  }, [props.listDateRange]);
  React.useEffect(() => {
    onListVisibleSelectionChange?.(filteredSelectionIds);
  }, [filteredSelectionIds, onListVisibleSelectionChange]);
  const updateSort = (nextSortKey: CalendarListSortKey) => {
    setPageIndex(0);

    if (nextSortKey === sortKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortKey(nextSortKey);
    setSortDirection("asc");
  };
  const renderSortHeader = (label: string, headerSortKey: CalendarListSortKey) => (
    <EntityModalButton
      kind="white"
      style={{ color: "#333", fontSize: "15px", textDecoration: "none" }}
      variant="link"
      onClick={() => updateSort(headerSortKey)}
    >
      {label}
      {sortKey === headerSortKey ? (
        <Text as="span" color="#777" fontSize="10px" lineHeight="1" ms={1}>
          {sortDirection === "asc" ? "▲" : "▼"}
        </Text>
      ) : null}
    </EntityModalButton>
  );
  const showSharedFilters = props.listVariant !== "caregiver";
  const showCaregiverColumns = props.listVariant === "caregiver";

  return (
    <Box>
      <Flex alignItems="flex-end" flexWrap="nowrap" gap={4} px="8px" py="24px">
        <Box flex="0 1 250px" minW="120px">
          <Text color="#0f172a" fontSize="14px" fontWeight={700} mb="8px">
            Pick Range
          </Text>
          <RangeDatePicker
            endDate={draftRange[1]}
            inputProps={{
              borderColor: "#ccc",
              borderRadius: "2px",
              color: "#555",
              fontSize: "15px",
              fontWeight: 600,
              h: "38px",
              maxW: "250px",
              minW: 0,
              w: "100%",
            }}
            popperPlacement="bottom-start"
            selected={draftRange[0]}
            startDate={draftRange[0]}
            onChange={(range) => {
              if (range === null) {
                const fallbackRange: [LocalDate, LocalDate] = [
                  LocalDate.now().withDayOfMonth(1),
                  LocalDate.now().plusMonths(1).withDayOfMonth(1).minusDays(1),
                ];

                setDraftRange(fallbackRange);
                setPageIndex(0);
                props.onListDateRangeChange?.(fallbackRange);
                return;
              }

              const selectedStartDate = range?.[0];

              if (selectedStartDate === undefined) {
                return;
              }

              const selectedRange: [LocalDate, LocalDate | null] = [
                selectedStartDate,
                range?.[1] ?? null,
              ];

              setDraftRange(selectedRange);

              if (selectedRange[1] === null) {
                return;
              }

              setPageIndex(0);
              props.onListDateRangeChange?.(selectedRange);
            }}
          />
        </Box>
        {showSharedFilters ? (
          <>
            <Box flex="0 1 180px" minW="110px">
              <Text color="#0f172a" fontSize="14px" fontWeight={700} mb="8px">
                Status
              </Text>
              <Select
                buttonProps={calendarListSelectButtonProps}
                label="Select"
                multiple={true}
                options={calendarListStatusOptions}
                searchable={false}
                selectedLabel={(value) =>
                  value.length === 1 ? value[0] : `${value.length} selected`
                }
                value={statusFilter}
                onChange={(value) => {
                  setStatusFilter(value ?? []);
                  setPageIndex(0);
                }}
              />
            </Box>
            <Box flex="0 1 180px" minW="110px">
              <Text color="#0f172a" fontSize="14px" fontWeight={700} mb="8px">
                Type
              </Text>
              <Select
                buttonProps={calendarListSelectButtonProps}
                label="Select"
                multiple={true}
                options={typeOptions}
                searchable={typeOptions.length > 5}
                selectedLabel={(value) =>
                  value.length === 1 ? value[0] : `${value.length} selected`
                }
                value={typeFilter}
                onChange={(value) => {
                  setTypeFilter(value ?? []);
                  setPageIndex(0);
                }}
              />
            </Box>
          </>
        ) : null}
        {props.listActionButtons ? (
          <Flex
            flex="1 1 auto"
            minW={0}
            overflowX="auto"
            sx={{ ...clippedActionsScrollSx, "> *": { marginInlineStart: "auto" } }}
          >
            {props.listActionButtons}
          </Flex>
        ) : null}
      </Flex>
      <Box minH="330px">
        {props.isLoading === false && filteredItems.length > 0 ? (
          <TableContainer overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th borderColor="#ddd" px="8px" w="44px" />
                  <Th {...getCalendarListHeaderStyles(sortKey === "date")}>
                    {renderSortHeader("Date", "date")}
                  </Th>
                  <Th {...getCalendarListHeaderStyles(sortKey === "caregiver")}>
                    {renderSortHeader(showCaregiverColumns ? "Patient" : "Caregiver", "caregiver")}
                  </Th>
                  {showCaregiverColumns ? (
                    <Th {...getCalendarListHeaderStyles(sortKey === "address")}>
                      {renderSortHeader("Address", "address")}
                    </Th>
                  ) : null}
                  <Th
                    borderColor="#ddd"
                    color="#333"
                    fontSize="15px"
                    fontWeight={400}
                    textTransform="none"
                  >
                    Schedule
                  </Th>
                  {showSharedFilters ? (
                    <>
                      <Th {...getCalendarListHeaderStyles(sortKey === "type")}>
                        {renderSortHeader("Type", "type")}
                      </Th>
                      <Th
                        borderColor="#ddd"
                        color="#333"
                        fontSize="15px"
                        fontWeight={400}
                        textTransform="none"
                      >
                        Status
                      </Th>
                    </>
                  ) : null}
                </Tr>
              </Thead>
              <Tbody>
                {visibleItems.map((item) => {
                  if (item.kind === "marker") {
                    const marker = item.marker;
                    const startDate = getCalendarListItemDate(item);
                    const endDate = getCalendarListItemEndDate(item);
                    const dateText = endDate.equals(startDate)
                      ? formatEntityCalendarDate(startDate)
                      : `${formatEntityCalendarDate(startDate)} - ${formatEntityCalendarDate(
                          endDate
                        )}`;

                    return (
                      <Tr key={`marker-${marker.id}`} _hover={{ bg: "#fafafa" }}>
                        <Td borderColor="#eee" px="8px" w="44px">
                          {props.onToggleMarkerSelection === undefined ? null : (
                            <Box
                              onClick={stopCalendarSelectionEvent}
                              onKeyDown={stopCalendarSelectionEvent}
                              onMouseDown={stopCalendarSelectionEvent}
                            >
                              <Checkbox
                                isChecked={selectedMarkerIds.has(String(marker.id))}
                                onChange={(event) => {
                                  event.stopPropagation();
                                  props.onToggleMarkerSelection?.(marker);
                                }}
                              />
                            </Box>
                          )}
                        </Td>
                        <Td borderColor="#eee" color="#777" fontSize="16px">
                          {dateText}
                        </Td>
                        <Td borderColor="#eee">
                          <Text color="#777" fontSize="16px" fontWeight={600}>
                            {marker.label}
                          </Text>
                        </Td>
                        {showCaregiverColumns ? <Td borderColor="#eee" /> : null}
                        <Td borderColor="#eee" color="#777" fontSize="16px">
                          <HStack flexWrap="wrap" spacing={2}>
                            <CalendarIcon color="#b8c0cc" />
                            <Text>{marker.detailLabel ?? marker.statusLabel ?? ""}</Text>
                            <MarkerActionLinks marker={marker} />
                          </HStack>
                        </Td>
                        {showSharedFilters ? (
                          <>
                            <Td borderColor="#eee" />
                            <Td borderColor="#eee">
                              <HStack flexWrap="wrap" spacing={2}>
                                {getCalendarListItemStatuses(item).map((status) => {
                                  const statusStyle = getCalendarListStatusStyle(status);

                                  return (
                                    <Box
                                      key={status}
                                      bg={statusStyle.backgroundColor}
                                      borderRadius="4px"
                                      color={statusStyle.color}
                                      display="inline-flex"
                                      fontSize="14px"
                                      fontWeight={600}
                                      px="14px"
                                      py="7px"
                                    >
                                      {status}
                                    </Box>
                                  );
                                })}
                              </HStack>
                            </Td>
                          </>
                        ) : null}
                      </Tr>
                    );
                  }

                  if (item.kind === "pto") {
                    const pto = item.pto;

                    return (
                      <Tr key={`pto-${pto.id}`} _hover={{ bg: "#fafafa" }}>
                        <Td borderColor="#eee" px="8px" w="44px">
                          <Checkbox isDisabled isChecked={false} />
                        </Td>
                        <Td borderColor="#eee" color="#777" fontSize="16px">
                          {formatEntityCalendarDate(pto.date)}
                        </Td>
                        <Td borderColor="#eee">
                          {pto.patientId != null ? (
                            <EntityModalButton
                              kind="blue"
                              style={{
                                color: "#337ab7",
                                fontSize: "16px",
                                textDecoration: "underline",
                              }}
                              variant="link"
                              onClick={(event) => {
                                event.stopPropagation();
                                props.onPatientClick?.(pto.patientId!);
                              }}
                            >
                              {pto.patientName ?? "Patient"}
                            </EntityModalButton>
                          ) : (
                            <Text color="#777" fontSize="16px">
                              {pto.patientName ?? "PTO"}
                            </Text>
                          )}
                        </Td>
                        {showCaregiverColumns ? (
                          <Td borderColor="#eee" color="#777" fontSize="16px">
                            {pto.patientAddress ?? ""}
                          </Td>
                        ) : null}
                        <Td borderColor="#eee" color="#777" fontSize="16px">
                          <HStack flexWrap="wrap" spacing={2}>
                            <CalendarIcon color="#b8c0cc" />
                            <Text>
                              {formatEntityCalendarTime(pto.startTime)}
                              {pto.endTime ? ` - ${formatEntityCalendarTime(pto.endTime)}` : ""}
                            </Text>
                            <Box
                              bg="#968CEC"
                              borderRadius="4px"
                              color="#fff"
                              display="inline-flex"
                              fontSize="13px"
                              fontWeight={600}
                              px="10px"
                              py="4px"
                            >
                              {pto.statusLabel ?? pto.label ?? "Paid time off"}
                            </Box>
                          </HStack>
                        </Td>
                        {showSharedFilters ? (
                          <>
                            <Td borderColor="#eee" color="#777" fontSize="16px">
                              {pto.certification ?? ""}
                            </Td>
                            <Td borderColor="#eee">
                              <Box
                                bg="#968CEC"
                                borderRadius="4px"
                                color="#fff"
                                display="inline-flex"
                                fontSize="14px"
                                fontWeight={600}
                                px="14px"
                                py="7px"
                              >
                                Paid time off
                              </Box>
                            </Td>
                          </>
                        ) : null}
                      </Tr>
                    );
                  }

                  if (item.kind === "vacation") {
                    return (
                      <Tr key={`vacation-${item.vacation.id}`} _hover={{ bg: "#fafafa" }}>
                        <Td borderColor="#eee" px="8px" w="44px">
                          <Box
                            onClick={stopCalendarSelectionEvent}
                            onKeyDown={stopCalendarSelectionEvent}
                            onMouseDown={stopCalendarSelectionEvent}
                          >
                            <Checkbox
                              isChecked={selectedVacationIds.has(item.vacation.id)}
                              onChange={(event) => {
                                event.stopPropagation();
                                props.onToggleVacationSelection?.(item.vacation);
                              }}
                            />
                          </Box>
                        </Td>
                        <Td borderColor="#eee" color="#777" fontSize="16px">
                          {formatEntityCalendarDate(item.vacation.date)}
                        </Td>
                        <Td borderColor="#eee" />
                        {showCaregiverColumns ? <Td borderColor="#eee" /> : null}
                        <Td borderColor="#eee" />
                        {showSharedFilters ? (
                          <>
                            <Td borderColor="#eee" />
                            <Td borderColor="#eee">
                              <HStack flexWrap="wrap" spacing={2}>
                                {getCalendarListItemStatuses(item).map((status) => {
                                  const statusStyle = getCalendarListStatusStyle(status);

                                  return (
                                    <Box
                                      key={status}
                                      bg={statusStyle.backgroundColor}
                                      borderRadius="4px"
                                      color={statusStyle.color}
                                      display="inline-flex"
                                      fontSize="14px"
                                      fontWeight={600}
                                      px="14px"
                                      py="7px"
                                    >
                                      {status}
                                    </Box>
                                  );
                                })}
                              </HStack>
                            </Td>
                          </>
                        ) : null}
                      </Tr>
                    );
                  }

                  const visit = item.visit;
                  const isSelectable = isVisitSelectable(visit);

                  return (
                    <Tr
                      key={visit.id}
                      _hover={{ bg: "#fafafa" }}
                      cursor={props.onVisitClick ? "pointer" : undefined}
                      onClick={() => props.onVisitClick?.(visit)}
                    >
                      <Td borderColor="#eee" px="8px" w="44px">
                        <Box
                          onClick={stopCalendarSelectionEvent}
                          onKeyDown={stopCalendarSelectionEvent}
                          onMouseDown={stopCalendarSelectionEvent}
                        >
                          <Checkbox
                            isChecked={selectedVisitIds.has(visit.id)}
                            isDisabled={!isSelectable}
                            onChange={(event) => {
                              event.stopPropagation();
                              if (isSelectable) {
                                props.onToggleVisitSelection?.(visit);
                              }
                            }}
                          />
                        </Box>
                      </Td>
                      <Td borderColor="#eee" color="#777" fontSize="16px">
                        {formatEntityCalendarDate(visit.date)}
                      </Td>
                      <Td borderColor="#eee">
                        <EntityModalButton
                          kind="blue"
                          style={{
                            color: "#337ab7",
                            fontSize: "16px",
                            textDecoration: "underline",
                          }}
                          variant="link"
                          onClick={(event) => {
                            event.stopPropagation();
                            if (showCaregiverColumns && visit.patientId != null) {
                              props.onPatientClick?.(visit.patientId);
                              return;
                            }

                            props.onVisitClick?.(visit);
                          }}
                        >
                          {visit.title}
                        </EntityModalButton>
                      </Td>
                      {showCaregiverColumns ? (
                        <Td borderColor="#eee" color="#777" fontSize="16px">
                          {visit.patientAddress ?? ""}
                        </Td>
                      ) : null}
                      <Td borderColor="#eee" color="#777" fontSize="16px">
                        <HStack spacing={2}>
                          <CalendarIcon color="#b8c0cc" />
                          <Text>
                            {formatEntityCalendarTime(visit.startTime)} -{" "}
                            {formatEntityCalendarTime(visit.endTime)}
                          </Text>
                        </HStack>
                      </Td>
                      {showSharedFilters ? (
                        <>
                          <Td borderColor="#eee" color="#777" fontSize="16px">
                            {visit.certification ?? ""}
                          </Td>
                          <Td borderColor="#eee">
                            <HStack flexWrap="wrap" spacing={2}>
                              {getCalendarListStatuses(visit).map((status) => {
                                const statusStyle = getCalendarListStatusStyle(status);
                                const statusTooltip = getCalendarListStatusTooltip(visit, status);

                                return (
                                  <Tooltip key={status} label={statusTooltip} placement="top">
                                    <Box
                                      bg={statusStyle.backgroundColor}
                                      borderRadius="4px"
                                      color={statusStyle.color}
                                      display="inline-flex"
                                      fontSize="14px"
                                      fontWeight={600}
                                      px="14px"
                                      py="7px"
                                    >
                                      {status}
                                    </Box>
                                  </Tooltip>
                                );
                              })}
                            </HStack>
                          </Td>
                        </>
                      ) : null}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        ) : null}
        {filteredItems.length === 0 && props.isLoading === false ? (
          <Center color={entityProfileModalColors.title} minH="260px" textAlign="center">
            <VStack spacing={5}>
              <Text fontSize="24px" fontWeight={300}>
                {props.listVariant === "caregiver"
                  ? "Caregiver does not have any visits for these dates"
                  : "Patient does not have any visits for these dates"}
              </Text>
              <CalendarIcon boxSize="76px" color={entityProfileModalColors.title} />
            </VStack>
          </Center>
        ) : null}
        {props.isLoading ? (
          <VStack color={entityProfileModalColors.title} minH="260px" pt="52px" spacing={6}>
            <Text fontSize="24px" fontWeight={300}>
              Loading Data
            </Text>
            <Progress isIndeterminate borderRadius="8px" colorScheme="blue" h="4px" w="120px" />
          </VStack>
        ) : null}
      </Box>
      <HStack justifyContent="flex-end" mt="16px" spacing={3}>
        <HStack spacing={0}>
          <EntityModalButton
            disabled={pageIndex === 0}
            kind="white"
            size="sm"
            onClick={() => setPageIndex((currentPageIndex) => Math.max(0, currentPageIndex - 1))}
          >
            Previous
          </EntityModalButton>
          <EntityModalButton
            disabled={pageIndex >= pageCount - 1}
            kind="white"
            size="sm"
            onClick={() =>
              setPageIndex((currentPageIndex) => Math.min(pageCount - 1, currentPageIndex + 1))
            }
          >
            Next
          </EntityModalButton>
        </HStack>
        <Text color="#4a5568" fontSize="14px">
          Page {pageIndex + 1} of {pageCount}
        </Text>
        <HStack spacing={0}>
          {calendarListPageSizes.map((pageSizeOption) => (
            <EntityModalButton
              key={pageSizeOption}
              kind="white"
              size="sm"
              style={{
                background: pageSizeOption === pageSize ? "#e5e5e5" : undefined,
                minWidth: 40,
              }}
              onClick={() => {
                setPageSize(pageSizeOption);
                setPageIndex(0);
              }}
            >
              {pageSizeOption}
            </EntityModalButton>
          ))}
        </HStack>
      </HStack>
    </Box>
  );
}

export default function EntityProfileCalendar(props: Props) {
  const sortedVisits = React.useMemo(
    () => [...props.visits].sort(compareVisitsByStartTime),
    [props.visits]
  );
  const showCalendarLoadingBar = props.view === "calendar" && props.isLoading;

  return (
    <VStack alignItems="stretch" spacing={2}>
      {props.view === "calendar" ? (
        <EntityProfileCalendarControls
          actionButtons={props.actionButtons}
          monthDate={props.monthDate}
          onChange={props.onMonthDateChange}
        />
      ) : null}
      {props.selectionControls}
      {props.view === "calendar" ? <CalendarLoadingBar isActive={showCalendarLoadingBar} /> : null}
      {props.isError ? (
        <EntityProfileErrorState label={props.errorLabel} onRetry={props.onRetry} />
      ) : null}
      {props.view === "calendar" ? (
        <CalendarGridView
          dayMenuItems={props.dayMenuItems}
          markers={props.markers}
          monthDate={props.monthDate}
          notes={props.notes}
          selectedMarkerIds={props.selectedMarkerIds ?? []}
          selectedVacationIds={props.selectedVacationIds ?? []}
          selectedVisitIds={props.selectedVisitIds ?? []}
          showAuthorizationDetails={props.showAuthorizationDetails ?? false}
          showCaregiverOvertimeAlerts={props.showCaregiverOvertimeAlerts ?? false}
          showSummaryColumn={props.showSummaryColumn ?? true}
          summaryVariant={props.summaryVariant ?? "default"}
          vacations={props.vacations}
          visibleDates={props.visibleDates}
          visits={sortedVisits}
          onToggleMarkerSelection={props.onToggleMarkerSelection}
          onToggleVacationSelection={props.onToggleVacationSelection}
          onToggleVisitSelection={props.onToggleVisitSelection}
          onVisitClick={props.onVisitClick}
          onVisitNameClick={props.onVisitNameClick}
        />
      ) : null}
      {props.view === "list" ? (
        <CalendarListView
          isLoading={props.isLoading}
          listActionButtons={props.listActionButtons}
          listCertificationOptions={props.listCertificationOptions}
          listDateRange={
            props.listDateRange ?? [
              props.visibleDates[0] ?? props.monthDate,
              props.visibleDates[props.visibleDates.length - 1] ?? props.monthDate,
            ]
          }
          listVariant={props.listVariant ?? "default"}
          markers={props.markers}
          ptoRows={props.ptoRows ?? []}
          selectedMarkerIds={props.selectedMarkerIds ?? []}
          selectedVacationIds={props.selectedVacationIds ?? []}
          selectedVisitIds={props.selectedVisitIds ?? []}
          vacations={props.vacations}
          visits={sortedVisits}
          onListDateRangeChange={props.onListDateRangeChange}
          onListVisibleSelectionChange={props.onListVisibleSelectionChange}
          onPatientClick={props.onPatientClick}
          onToggleMarkerSelection={props.onToggleMarkerSelection}
          onToggleVacationSelection={props.onToggleVacationSelection}
          onToggleVisitSelection={props.onToggleVisitSelection}
          onVisitClick={props.onVisitClick}
          onVisitNameClick={props.onVisitNameClick}
        />
      ) : null}
    </VStack>
  );
}
